import {
  RangeSlider,
  RangeSliderFilledTrack,
  RangeSliderMark,
  RangeSliderThumb,
  RangeSliderTrack,
  Text,
} from '@chakra-ui/react';
import { InputNumber } from 'antd';
import { createParser, useQueryState } from 'nuqs';
import React, { useTransition } from 'react';

import { useClearSearch } from '../utils';

import styles from './min-max-slider.module.scss';

export const MinMaxFilter: React.FC<{
  min: number;
  max: number;
  queryKey: string;
  label?: string;
  step?: number;
}> = ({ min, max, queryKey, label, step = 1, }) => {
  const [_, startTransition] = useTransition();
  const isSingleValue = min === max;

  const [from, setFrom] = useQueryState(
    `${queryKey}from`,
    createParser({
      parse: (value) => (value ? parseInt(value, 10) : min),
      serialize: (value) => value.toString(),
    })
      .withDefault(min)
      .withOptions({
        startTransition,
        history: 'replace',
        throttleMs: 300,
        shallow: false,
      }),
  );

  const [to, setTo] = useQueryState(
    `${queryKey}to`,
    createParser({
      parse: (value) => (value ? parseInt(value, 10) : max),
      serialize: (value) => value.toString(),
    })
      .withDefault(max)
      .withOptions({
        startTransition,
        history: 'replace',
        throttleMs: 300,
        shallow: false,
      }),
  );

  const clearSearch = useClearSearch();

  const updateFrom = async (value: number) => {
    clearSearch();
    await setFrom(value);
  };

  const updateTo = async (value: number) => {
    clearSearch();
    await setTo(value);
  };

  const updateBothValues = async (min: number, max: number) => {
    clearSearch();
    await setFrom(min);
    await setTo(max);
  };

  return (
    <div>
      <div className={styles.rangeWrapper}>
        {label && (
          <div className={styles.filterLabel}>
            <Text className={styles.labelText}>{label}</Text>
          </div>
        )}
        <div className={styles.rangeInputs}>
          <InputNumber
            className={styles.rangeInput}
            value={from}
            onChange={(val) => {
              const newMin = val || 0;

              if (newMin <= to) {
                updateFrom(newMin);
              }
            }}
            min={min}
            max={max}
            disabled={true}
          />
          <span className={styles.separator}>-</span>
          <InputNumber
            className={styles.rangeInput}
            value={to}
            onChange={(val) => {
              const newMax = val || 0;

              if (newMax >= from) {
                updateTo(newMax);
              }
            }}
            min={min}
            max={max}
            disabled={true}
          />
        </div>
      </div>

      <RangeSlider
        step={step}
        defaultValue={[min, max]}
        min={min}
        max={max}
        value={[from, to]}
        onChange={([newMin, newMax]) => {
          updateBothValues(newMin || min, newMax || max);
        }}
        mb={10}
        mt={4}
        isDisabled={isSingleValue}
      >
        <RangeSliderTrack bg="gray.100">
          <RangeSliderFilledTrack bg="black" />
        </RangeSliderTrack>
        <RangeSliderThumb
          boxSize={6}
          index={0}
          borderWidth="1px"
          borderColor="black"
        />
        <RangeSliderThumb
          boxSize={6}
          index={1}
          borderWidth="1px"
          borderColor="black"
        />
        {isSingleValue ? (
          <RangeSliderMark
            value={min}
            mt={4}
            textAlign="center"
            transform="translateX(-50%)"
          >
            <Text className={styles.priceFormatText}>{min}</Text>
          </RangeSliderMark>
        ) : (
          <>
            <RangeSliderMark
              value={min}
              textAlign="left"
              mt={4}
              transform="translateX(calc(-6px))"
            >
              <Text className={styles.priceFormatText}>{min}</Text>
            </RangeSliderMark>
            <RangeSliderMark
              value={max}
              textAlign="right"
              width="max-content"
              transform="translateX(calc(18px - 100%))"
              mt={4}
            >
              <Text className={styles.priceFormatText}>{max}</Text>
            </RangeSliderMark>
          </>
        )}
      </RangeSlider>
    </div>
  );
};
